import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useStytchWorkspaces } from '@/hooks/useStytchWorkspaces'
import { Check } from 'lucide-react'
import * as React from 'react'
import { useParams } from 'react-router-dom'

import type { Organization } from '@stytch/vanilla-js'

export const WorkspaceGeneralSettings: React.FC = () => {
	const { workspaceId = '' } = useParams()
	const { workspaces, updateWorkspace, isUpdating } = useStytchWorkspaces()
	const [organizationName, setOrganizationName] = React.useState('')
	const [showSuccess, setShowSuccess] = React.useState(false)

	// Get current workspace
	const workspaceMap = workspaces.reduce(
		(mp, workspace) => {
			mp[workspace.organization_slug] = workspace
			return mp
		},
		{} as Record<string, Organization>
	)

	const currentWorkspace = workspaceMap[workspaceId] ?? workspaces[0]

	// Update local state when workspace changes
	React.useEffect(() => {
		if (currentWorkspace) {
			setOrganizationName(currentWorkspace.organization_name)
		}
	}, [currentWorkspace])

	const handleNameBlur = async () => {
		if (!currentWorkspace || organizationName === currentWorkspace.organization_name) {
			return
		}

		try {
			await updateWorkspace({
				organization_name: organizationName,
			})
			// Show success indicator
			setShowSuccess(true)
			// Hide success indicator after 2 seconds
			setTimeout(() => setShowSuccess(false), 2000)
		} catch (error) {
			console.error('Failed to update workspace name:', error)
			// Reset to original value on error
			setOrganizationName(currentWorkspace.organization_name)
		}
	}

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setOrganizationName(e.target.value)
	}

	if (!currentWorkspace) {
		return (
			<div className="flex items-center justify-center p-8">
				<p className="text-muted-foreground">No workspace found</p>
			</div>
		)
	}

	return (
		<div className="space-y-6 p-3">
			<div>
				<h2 className="text-2xl font-bold tracking-tight">General Settings</h2>
				<p className="text-muted-foreground">Manage your workspace settings and preferences.</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Workspace Information</CardTitle>
					<CardDescription>Update your workspace name and other basic information.</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="organization-name">Workspace Name</Label>
						<div className="relative">
							<Input
								id="organization-name"
								value={organizationName}
								onChange={handleNameChange}
								onBlur={handleNameBlur}
								placeholder="Enter workspace name"
								disabled={isUpdating}
								className="pr-10"
							/>
							{showSuccess && (
								<Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
							)}
						</div>
						<p className="text-sm text-muted-foreground">
							This is the name that will be displayed throughout the application.
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="organization-id">Workspace ID</Label>
						<Input
							id="organization-id"
							value={currentWorkspace.organization_id}
							disabled
							className="bg-muted"
						/>
						<p className="text-sm text-muted-foreground">
							This ID cannot be changed and is used internally.
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="organization-slug">Workspace Slug</Label>
						<Input
							id="organization-slug"
							value={currentWorkspace.organization_slug}
							disabled
							className="bg-muted"
						/>
						<p className="text-sm text-muted-foreground">
							This is the unique identifier for your workspace in URLs.
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
